/** @forge-header
 * @id          cfbParser
 * @role        College Football ticker parser | game, spread, and total markets
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo | kalshiTicker/helpers:parseFallback,splitCompetitors
 * @downstream  kalshiTicker/parse
 * @exports     parseCfbGame:fn | parseCfbSpread:fn | parseCfbTotal:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, GameInfo, TickerCategory } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback, splitCompetitors } from '../helpers';

/**
 * College Football: KXNCAAFGAME-26SEP19MSUND-ND
 * Team-vs-team moneyline with variable-length team codes — uses the outcome
 * code to anchor the away/home split. No team-name registry (codes shown
 * as-is), like NCAA basketball.
 */
export function parseCfbGame(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) return parseFallback(raw);

  const mid = parts[1] ?? '';
  if (mid.length < 9) return parseFallback(raw); // 7 date + >=1+1 codes

  const date = parseDateFromTicker(mid.slice(0, 7));
  const outcome = parts[2] ?? null;
  const [awayCode, homeCode] = splitCompetitors(mid.slice(7), outcome);

  const game: GameInfo = { awayCode, homeCode };
  const shortLabel = `CFB: ${awayCode} @ ${homeCode}`;
  const fullLabel = outcome
    ? `CFB: ${awayCode} @ ${homeCode} · ${outcome} Win`
    : `CFB: ${awayCode} @ ${homeCode}`;

  return {
    raw,
    category: 'cfb-game',
    series: 'KXNCAAFGAME',
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome,
    shortLabel,
    fullLabel,
  };
}

/**
 * CFB spread: KXNCAAFSPREAD-26AUG29UNCTCU-TCU8
 *
 * The selected outcome carries the favored team plus an integer bucket. That
 * team token is the only ticker-owned evidence that can safely split unequal
 * college codes (UNC/TCU, SJSU/USC). If it does not bound the packed matchup,
 * leave the game unresolved rather than midpoint-splitting to a plausible but
 * wrong pair. describeMarket can still recover from producer-owned canonical
 * away/home identity when the live frame is present.
 */
export function parseCfbSpread(raw: string): ParsedTicker {
  const outcome = raw.split('-')[2]?.toUpperCase() ?? null;
  const favoredTeam = outcome?.match(/^([A-Z]+)\d+$/)?.[1] ?? null;
  if (!favoredTeam) return parseFallback(raw);
  return parseCfbLine(raw, 'KXNCAAFSPREAD', 'cfb-spread', favoredTeam);
}

/**
 * CFB total: KXNCAAFTOTAL-26AUG29SJSUUSC-41
 *
 * A total's trailing outcome is only the strike bucket; the ticker contains no
 * team anchor. Preserve the category/date/outcome but intentionally leave the
 * matchup unresolved. describeMarket accepts the line only when Hydra's
 * canonical away/home identity is available.
 */
export function parseCfbTotal(raw: string): ParsedTicker {
  return parseCfbLine(raw, 'KXNCAAFTOTAL', 'cfb-total', null);
}

function parseCfbLine(
  raw: string,
  series: 'KXNCAAFSPREAD' | 'KXNCAAFTOTAL',
  category: TickerCategory,
  anchorTeam: string | null,
): ParsedTicker {
  const parts = raw.split('-');
  const mid = parts[1] ?? '';
  if (parts.length < 3 || mid.length < 9) return parseFallback(raw);

  const date = parseDateFromTicker(mid.slice(0, 7));
  if (!date) return parseFallback(raw);

  const packed = mid.slice(7);
  let game: GameInfo | null = null;
  if (anchorTeam) {
    if (packed.startsWith(anchorTeam) && packed.length > anchorTeam.length) {
      game = { awayCode: anchorTeam, homeCode: packed.slice(anchorTeam.length) };
    } else if (packed.endsWith(anchorTeam) && packed.length > anchorTeam.length) {
      game = { awayCode: packed.slice(0, -anchorTeam.length), homeCode: anchorTeam };
    }
  }

  const outcome = parts[2]?.toUpperCase() ?? null;
  const kind = category === 'cfb-spread' ? 'Spread' : 'Total';
  const matchup = game ? `${game.awayCode} @ ${game.homeCode}` : raw;

  return {
    raw,
    category,
    series,
    date,
    game,
    player: null,
    spread:
      category === 'cfb-spread'
        ? { favoredTeam: anchorTeam ?? '', spreadValue: 0, side: 'cover' }
        : null,
    total:
      category === 'cfb-total'
        ? { totalValue: 0, side: 'over' }
        : null,
    tennis: null,
    outcome,
    shortLabel: `CFB ${kind}: ${matchup}`,
    fullLabel: `CFB ${kind}: ${matchup}${outcome ? ` · ${outcome}` : ''}`,
  };
}
